/**
 * Pointer utilities for the sd-patch engine.
 *
 * Pointers are RFC-6901-style JSON pointers extended with keyed-array selectors:
 * - plain segments address object keys ("~" escapes to "~0", "/" to "~1");
 *   an all-digit plain segment addresses an array index when the container is an array;
 * - `[name=X]` / `[id=X]` segments address the element of an array whose `name` / `id`
 *   member matches. Selector values are taken RAW between "=" and "]" (the differ
 *   refuses to emit selectors whose key values contain "/" or "]").
 *
 * `[id=X]` matching uses `normalizeNodeId` semantics, so a numeric `5` and a string
 * `"5"` are the same id — the tree model declares numeric ids but strings occur at
 * runtime. `[name=X]` matching is a plain string comparison.
 */

import { CollabJsonValue, ICollabJsonObject, isJsonArray, isJsonObject } from "./sd-json";

/** Canonical string form of a display-item id ("" for null/undefined). */
export function normalizeNodeId(id: number | string | undefined | null): string {
  if (id === undefined || id === null) {
    return "";
  }
  return String(id);
}

/**
 * Turns a canonical string id back into the representation the tree stores:
 * a number when it parses as a safe integer, otherwise the string itself.
 */
export function denormalizeNodeId(idNorm: string): number | string {
  if (/^-?\d+$/.test(idNorm)) {
    const parsed: number = Number(idNorm);
    if (Number.isSafeInteger(parsed)) {
      return parsed;
    }
  }
  return idNorm;
}

export interface IPlainPathSegment {
  kind: "plain";
  key: string;
}

export interface ISelectorPathSegment {
  kind: "selector";
  /** "name" or "id". */
  selectorKey: string;
  /** Raw key value (no escaping applied). */
  selectorValue: string;
}

export type CollabPathSegment = IPlainPathSegment | ISelectorPathSegment;

const SELECTOR_SEGMENT_PATTERN: RegExp = /^\[(name|id)=([\s\S]*)\]$/;

/** An all-digit plain segment addresses an array index when the container is an array. */
export const ARRAY_INDEX_PATTERN: RegExp = /^\d+$/;

/**
 * Object keys that must never appear as a plain pointer segment. Patches arrive from
 * untrusted sources (collab peers / LLM-generated ops), and both `setAtPath` (write) and
 * `walkToParent` (traverse) assign/step through `container[key]`. Allowing `__proto__`,
 * `constructor`, or `prototype` would let a crafted path walk onto `Object.prototype` and
 * pollute it (CWE-1321). Rejecting these keys at parse time fails such pointers closed for
 * every consumer (getAtPath / setAtPath / removeAtPath, sd-patch and wf-patch alike).
 */
const FORBIDDEN_PLAIN_SEGMENT_KEYS: ReadonlySet<string> = new Set<string>(["__proto__", "prototype", "constructor"]);

/** Whether `key` is a prototype-pollution vector that must not be used as an object key. */
export function isForbiddenPlainSegmentKey(key: string): boolean {
  return FORBIDDEN_PLAIN_SEGMENT_KEYS.has(key);
}

export function escapePointerSegment(segment: string): string {
  return segment.replace(/~/g, "~0").replace(/\//g, "~1");
}

export function unescapePointerSegment(segment: string): string {
  return segment.replace(/~1/g, "/").replace(/~0/g, "~");
}

/**
 * Parses a pointer into segments. Returns null for malformed pointers
 * (no leading "/", or an empty segment). "" parses to the empty segment list.
 */
export function parsePointer(path: string): CollabPathSegment[] | null {
  if (path === "") {
    return [];
  }
  if (!path.startsWith("/")) {
    return null;
  }
  const rawSegments: string[] = path.slice(1).split("/");
  const segments: CollabPathSegment[] = [];
  for (const raw of rawSegments) {
    if (raw === "") {
      return null;
    }
    const selectorMatch: RegExpExecArray | null = SELECTOR_SEGMENT_PATTERN.exec(raw);
    if (selectorMatch !== null) {
      segments.push({ kind: "selector", selectorKey: selectorMatch[1], selectorValue: selectorMatch[2] });
    } else {
      const key: string = unescapePointerSegment(raw);
      if (isForbiddenPlainSegmentKey(key)) {
        return null;
      }
      segments.push({ kind: "plain", key });
    }
  }
  return segments;
}

export function buildPointer(segments: readonly CollabPathSegment[]): string {
  let pointer: string = "";
  for (const segment of segments) {
    if (segment.kind === "selector") {
      pointer += `/[${segment.selectorKey}=${segment.selectorValue}]`;
    } else {
      pointer += `/${escapePointerSegment(segment.key)}`;
    }
  }
  return pointer;
}

/** Whether `element` answers to a `[name=X]` / `[id=X]` selector (id via `normalizeNodeId`). */
export function selectorMatchesElement(element: CollabJsonValue, segment: ISelectorPathSegment): boolean {
  if (!isJsonObject(element)) {
    return false;
  }
  const keyValue: CollabJsonValue = element[segment.selectorKey];
  if (segment.selectorKey === "id") {
    if (typeof keyValue !== "string" && typeof keyValue !== "number") {
      return false;
    }
    return normalizeNodeId(keyValue) === segment.selectorValue;
  }
  return typeof keyValue === "string" && keyValue === segment.selectorValue;
}

/** Index of the first element matching a keyed selector; -1 when none matches. */
export function findSelectorIndex(array: readonly CollabJsonValue[], segment: ISelectorPathSegment): number {
  for (let i = 0; i < array.length; i++) {
    if (selectorMatchesElement(array[i], segment)) {
      return i;
    }
  }
  return -1;
}

/** Resolves the value at `path` inside `root`; undefined when unresolvable. */
export function getAtPath(root: unknown, path: string): unknown {
  const segments: CollabPathSegment[] | null = parsePointer(path);
  if (segments === null) {
    return undefined;
  }
  let current: unknown = root;
  for (const segment of segments) {
    if (segment.kind === "selector") {
      if (!isJsonArray(current)) {
        return undefined;
      }
      const selectorIndex: number = findSelectorIndex(current, segment);
      if (selectorIndex < 0) {
        return undefined;
      }
      current = current[selectorIndex];
    } else if (isJsonArray(current)) {
      if (!ARRAY_INDEX_PATTERN.test(segment.key)) {
        return undefined;
      }
      const index: number = Number(segment.key);
      if (index >= current.length) {
        return undefined;
      }
      current = current[index];
    } else if (isJsonObject(current)) {
      current = current[segment.key];
    } else {
      return undefined;
    }
  }
  return current;
}

/**
 * Walks to the container that owns the final segment.
 * `createMissing` (setAtPath only) creates `{}` at plain object segments whose
 * current value is missing (undefined/null). Intermediate selectors that do not
 * match an existing element always fail. Returns null on failure.
 */
function walkToParent(root: unknown, segments: readonly CollabPathSegment[], createMissing: boolean): unknown {
  let current: unknown = root;
  for (let i = 0; i < segments.length - 1; i++) {
    const segment: CollabPathSegment = segments[i];
    if (segment.kind === "selector") {
      if (!isJsonArray(current)) {
        return null;
      }
      const selectorIndex: number = findSelectorIndex(current, segment);
      if (selectorIndex < 0) {
        return null;
      }
      current = current[selectorIndex];
    } else if (isJsonArray(current)) {
      if (!ARRAY_INDEX_PATTERN.test(segment.key)) {
        return null;
      }
      const index: number = Number(segment.key);
      if (index >= current.length) {
        return null;
      }
      current = current[index];
    } else if (isJsonObject(current)) {
      const existing: CollabJsonValue = current[segment.key];
      if (existing === undefined || existing === null) {
        if (!createMissing) {
          return null;
        }
        const created: ICollabJsonObject = {};
        current[segment.key] = created;
        current = created;
      } else {
        current = existing;
      }
    } else {
      return null;
    }
  }
  return current;
}

/**
 * Sets `value` at `path` inside `root`, creating missing intermediate OBJECTS for
 * plain segments. A final keyed selector that matches replaces the element; one
 * that does not match APPENDS `value` to the array. Returns success.
 */
export function setAtPath(root: unknown, path: string, value: unknown): boolean {
  const segments: CollabPathSegment[] | null = parsePointer(path);
  if (segments === null || segments.length === 0) {
    return false;
  }
  const container: unknown = walkToParent(root, segments, true);
  if (container === null || container === undefined) {
    return false;
  }
  const last: CollabPathSegment = segments[segments.length - 1];
  const payload: CollabJsonValue = value as CollabJsonValue;
  if (last.kind === "selector") {
    if (!isJsonArray(container)) {
      return false;
    }
    const selectorIndex: number = findSelectorIndex(container, last);
    if (selectorIndex >= 0) {
      container[selectorIndex] = payload;
    } else {
      container.push(payload);
    }
    return true;
  }
  if (isJsonArray(container)) {
    if (!ARRAY_INDEX_PATTERN.test(last.key)) {
      return false;
    }
    const index: number = Number(last.key);
    if (index > container.length) {
      return false;
    }
    container[index] = payload;
    return true;
  }
  if (isJsonObject(container)) {
    container[last.key] = payload;
    return true;
  }
  return false;
}

/**
 * Removes the value at `path` inside `root`. Never creates intermediates.
 * Array targets are spliced out; object keys are deleted. Returns success
 * (false when the target does not exist).
 */
export function removeAtPath(root: unknown, path: string): boolean {
  const segments: CollabPathSegment[] | null = parsePointer(path);
  if (segments === null || segments.length === 0) {
    return false;
  }
  const container: unknown = walkToParent(root, segments, false);
  if (container === null || container === undefined) {
    return false;
  }
  const last: CollabPathSegment = segments[segments.length - 1];
  if (last.kind === "selector") {
    if (!isJsonArray(container)) {
      return false;
    }
    const selectorIndex: number = findSelectorIndex(container, last);
    if (selectorIndex < 0) {
      return false;
    }
    container.splice(selectorIndex, 1);
    return true;
  }
  if (isJsonArray(container)) {
    if (!ARRAY_INDEX_PATTERN.test(last.key)) {
      return false;
    }
    const index: number = Number(last.key);
    if (index >= container.length) {
      return false;
    }
    container.splice(index, 1);
    return true;
  }
  if (isJsonObject(container)) {
    if (!(last.key in container)) {
      return false;
    }
    delete container[last.key];
    return true;
  }
  return false;
}
